// @ts-check
import React, { Component } from 'react';
import hoistStatics from 'hoist-non-react-statics';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';

const controlledTabify = originalComponent =>
  class extends Component {
    static displayName = `tabified ${originalComponent.name}`;

    static propTypes = {
      activeTabName: PropTypes.string.isRequired,
      setActiveTabName: PropTypes.func.isRequired,
      children: PropTypes.node.isRequired,
    };

    render() {
      const children = React.Children.toArray(this.props.children).filter(c => c); // Remove falsy children (i.e. null)
      const propsToInject = {
        tabs: children.map(child => ({
          name: child.props.tabName,
          onClick: () =>
            child.props.linkTo
              ? browserHistory.push(child.props.linkTo)
              : this.props.setActiveTabName(child.props.tabName),
          active: child.props.tabName === this.props.activeTabName,
          ...child.props,
        })),
        setActiveTabName: this.props.setActiveTabName,
      };

      let wrappedChildren;
      if (this.props.unmountInactive) {
        wrappedChildren = (
          <div>{children.filter(child => child.props.tabName === this.props.activeTabName)}</div>
        );
      } else {
        wrappedChildren = children.map(child => (
          <div
            key={child.props.tabName}
            style={child.props.tabName === this.props.activeTabName ? {} : { display: 'none' }}
          >
            {child}
          </div>
        ));
      }

      let decoratedComponent = React.createElement(
        originalComponent,
        { ...this.props, ...propsToInject },
        wrappedChildren
      );

      decoratedComponent = hoistStatics(decoratedComponent, originalComponent);

      return decoratedComponent;
    }
  };

const uncontrolledTabify = originalComponent => {
  const intermediateComponent = controlledTabify(originalComponent);

  return hoistStatics(
    class extends Component {
      static displayName = `uncontrolled ${intermediateComponent.displayName}`;

      static propTypes = {
        defaultTabName: PropTypes.string,
        children: PropTypes.node.isRequired,
      };

      state = {
        activeTabName:
          this.props.defaultTabName || React.Children.toArray(this.props.children)[0].props.tabName,
      };

      setActiveTabName = tabName => this.setState({ activeTabName: tabName });

      render() {
        const propsToInject = {
          activeTabName: this.props.activeTabName || this.state.activeTabName,
          setActiveTabName: this.setActiveTabName,
        };

        return React.createElement(
          intermediateComponent,
          { ...this.props, ...propsToInject },
          this.props.children
        );
      }
    },
    originalComponent
  );
};

export { controlledTabify, uncontrolledTabify };
export default uncontrolledTabify;
